import { useEffect, useMemo } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  type MailingAddressFormData,
  toCreateMailingAddressReq,
  toUpdateMailingAddressReq,
} from "src/api/adapters/address";
import {
  AddressFields,
  FormTable,
  FormTableBody,
  Location06Icon,
} from "@porchpass/ui";
import { DomainAddressType } from "@porchpass/api-client";
import {
  addressErrorsAfterSubmit,
  fromUiAddressPatch,
  toUiAddress,
} from "src/forms/adapters/address";
import { googlePlacesFieldProps } from "src/constants/googlePlaces";
import {
  lessThanMaxLength,
  notEmpty,
  validStreetAddress,
  validZipcode,
  validate,
  test,
  type ValidationMapperFunc,
} from "src/utils";

const validationMapper: ValidationMapperFunc<MailingAddressFormData> = (_) => ({
  street1: [
    test(notEmpty, "Address must not be empty"),
    test(validStreetAddress, "Must be a valid street address"),
    test(lessThanMaxLength(120), "Address must not exceed 120 characters"),
  ],
  city: [
    test(notEmpty, "City must not be empty"),
    test(lessThanMaxLength(70), "City must not exceed 70 characters"),
  ],
  state: [test(notEmpty, "State must not be empty")],
  zip_code: [
    test(notEmpty, "Zip Code must not be empty"),
    test(validZipcode, "Must be a valid US zip code"),
  ],
});

type MailingAddressModalProps = {
  address?: MailingAddressFormData & { id?: string };
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  address,
  closeHandler,
  confirmHandler,
}: MailingAddressModalProps) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<MailingAddressFormData>(
    { ...address },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: MailingAddressFormData) =>
      address?.id
        ? api.updateAddress(address.id, toUpdateMailingAddressReq(data))
        : api.createAddress(toCreateMailingAddressReq(data)),
    );

  useEffect(() => {
    if (response) {
      confirmHandler();
    }
  }, [response]);

  const formErrors = validate<MailingAddressFormData>(
    formData,
    validationMapper,
  );

  const addressValue = useMemo(
    () => toUiAddress(formData, DomainAddressType.AddressMailing),
    [formData],
  );

  const addressErrors = useMemo(
    () => addressErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyAddressChange = (patch: Partial<typeof addressValue>) => {
    const mapped = fromUiAddressPatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      if (val !== undefined) {
        updateField(key as keyof MailingAddressFormData)(val);
      }
    }
  };

  return (
    <SlideUpModal
      title="What's your mailing address?"
      isOpen={true}
      openSession={address}
      closeHandler={closeHandler}
      hasDismissButton={true}
      actionButtonText={address?.id ? "Save" : "Add Address"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Mailing Address"
        headerIcon={
          <Location06Icon
            variant="bulk-rounded"
            size={16}
            className="text-primary"
          />
        }
      >
        <FormTableBody>
          <AddressFields
            address={addressValue}
            errors={addressErrors}
            onFieldChange={(field, value) => {
              applyAddressChange({ [field]: value });
            }}
            onBulkChange={applyAddressChange}
            {...googlePlacesFieldProps}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
